import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import axios from 'axios';
import Mitigation from '../../Dunder/Mitigation.pdf';
import Adjuster from '../../Dunder/Adjuster.pdf';
import '../../css/ActiveClaims.css';
import '../../css/AdjustmentsActiveClaims.css';
import MainWrapper from '../wrappers/mainWrapper';
import { createPortal } from 'react-dom';

const ITEMS_PER_PAGE = 11;

const ActiveClaims = () => {
  const [loading, setLoading] = useState(true);
  const [comps, setComps] = useState([]);
  const [expandedRowIndex, setExpandedRowIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [customer, setCustomer] = useState();
  const [subscription, setSubscription] = useState();
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [hoveredCell, setHoveredCell] = useState({ id: null, content: '' });
  const [errorPopup, setErrorPopup] = useState({ show: false, content: '', compId: '' });

  const [openMenu, setOpenMenu] = useState({ index: null, top: 0, left: 0 });

  const user = useSelector((state) => state.user);
  const userData = useSelector((state) => state.user._id);
  const userEmail = useSelector((state) => state.user.email);

  // ---------- FETCH COMPARISONS ----------
  const getComparisons = useCallback(async (page = 1) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_GET_COMPARISONS}${userData}?page=${page}&limit=${ITEMS_PER_PAGE}`
      );
      const data = await response.json();
      setComps(data.comparisons || []);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching comparisons:', error);
      setComps([]);
    } finally {
      setLoading(false);
    }
  }, [userData]);

  // ---------- FETCH CUSTOMER & SUBSCRIPTION ----------
  const getCustomerDetails = useCallback(async () => {
    try {
      const customerResponse = await axios.get(`${process.env.REACT_APP_CUSTOMER_EMAIL}`, {
        params: { email: userEmail },
      });
      setCustomer(customerResponse.data);

      const subscriptionResponse = await axios.get(
        `${process.env.REACT_APP_GET_CUSTOMER_SUBSCRIPTIONS}${user?.stripeCustomer?.id}`
      );
      setSubscription(subscriptionResponse.data.subscriptions);
    } catch (err) {
      console.error('Error fetching customer details:', err);
    }
  }, [userEmail, user?.stripeCustomer?.id]);

  // ---------- INITIAL & INTERVAL FETCH ----------
  useEffect(() => {
    let interval = null;

    const fetchData = async () => {
      await getComparisons(currentPage);
      await getCustomerDetails();
    };

    fetchData();
    interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [currentPage, getComparisons, getCustomerDetails]);

  // ---------- DATE UTILITY ----------
  const checkDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d)) return '-';
    return `${(d.getUTCMonth() + 1).toString().padStart(2, '0')}/${d
      .getUTCDate()
      .toString()
      .padStart(2, '0')}/${d.getUTCFullYear()}`;
  };

  const changeStatusOutput = (item) => {
    switch (item.status) {
      case 'Finished':
        return 'Finished';
      case 'Pending':
        return 'Pending';
      case 'Failed':
        return 'This one stumped me.';
      case 'Archived':
        return 'Archived';
      case 'Queue':
        return 'In Queue';
      default:
        return '-';
    }
  };

  const getMessage = (item) => item.statusMSG || '';

  const extractProgress = (message) => {
    const match = message.match(/(\d+)%/);
    return match ? parseInt(match[1], 10) : 0;
  };

  // ---------- USER FRIENDLY ERROR MESSAGE ----------
  const friendlyErrorMessage = (rawMsg, compId) => {
    if (!rawMsg) return `Support has been notified of the issue. Thank you for your patience.`;

    const msg = rawMsg.toLowerCase();
    let userMsg = '';

    if (msg.includes('no line items found')) {
      userMsg = 'Please ensure that your documents both have enumerated line items.';
    } else if (msg.includes('invalid est_headers_list') || msg.includes('invalid ins_headers_list')) {
      userMsg = 'Make sure your documents are properly formatted - Preferrably, a Final Draft Xactimate.';
    } else if (msg.includes('invalid or corrupt pdf file')) {
      userMsg = 'Please make sure your PDF is not corrupted. Try opening the file.';
    } else {
      userMsg = 'An unexpected issue occurred.';
    }

    return `${userMsg} Support has been notified of the issue. Thank you for your patience.`;
  };

  const copyComparisonId = (compId) => {
    navigator.clipboard.writeText(compId);
    alert('Comparison ID copied!');
  };

  const deleteComparison = (id) => {
    axios
      .delete(`${process.env.REACT_APP_DELETE_COMPARISON}${id}`)
      .then(() => console.log('Delete Successful'))
      .catch(() => console.log('Delete Failed'));
  };

  // ---------- SUBSCRIPTION / FREE VIEW ----------
  const now = new Date();

  const isTrialing =
    user &&
    user.trial_start &&
    user.trial_end &&
    user.trial_over === false &&
    new Date(user.trial_end) > now;

  const isFreeViewMode =
    !isTrialing &&
    !(user?.baseSubscription?.status === 'active') &&
    !user?.admin &&
    !user?.email?.includes('@pdgcreative.com');

  // ---------- COMPARISON STATUS ----------
  const checkComparisonStatus = (item) => {
    switch (true) {
      case item.status === 'Pending':
        return <button className="comparison-pending-button">PENDING</button>;
      case item.status === 'Failed':
        return <span>RESULTS UNAVAILABLE</span>;
      case item.status === 'Archived':
        return <span>RESULTS ARCHIVED</span>;
      case item.status === 'Finished':
        if (isFreeViewMode) {
          return (
            <button className="view-results-button">
              <NavLink to={`/limitedResults/${item._id}`} className="view-results-button-nav white">
                VIEW RESULTS
              </NavLink>
            </button>
          );
        }
        const hasDetectedZones = item?.cleanParsedEstData?.detectedZones;
        return (
          <button
            className="view-results-button"
          >
            <NavLink
              to={hasDetectedZones ? `/restructuredResults/${item._id}` : `/comparisonResults/${item._id}`}
              style={{color: 'white', textDecoration: 'none'}}
            >
              VIEW RESULTS
            </NavLink>
          </button>
        );
      default:
        return <span>-</span>;
    }
  };

  // ---------- MEATBALL MENU ----------
  const toggleMenu = (e, index) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (openMenu.index === index) {
      setOpenMenu({ index: null, top: 0, left: 0 });
    } else {
      setOpenMenu({
        index,
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  };

  const renderActionsMenu = (item, index) => {
    const allowedDomains = ['@pdgcreative.com', '@getlevlr.com', '@elkmontllc.com'];
    const canViewDetails = allowedDomains.some((domain) => userEmail.endsWith(domain));
    const canDelete = user?.email.includes('pdgcreative.com');

    if (canViewDetails || canDelete) {
      return (
        <div className="meatball-container">
          <button className="meatball-button" onClick={(e) => toggleMenu(e, index)}>
            ⋮
          </button>
        </div>
      );
    }

    return checkComparisonStatus(item);
  };
  const newComparison = async (e) => {
    e.preventDefault();
    try {
        // Load local PDFs into File objects
        const mitigationBlob = await fetch(Mitigation).then(res => res.blob());
        const adjusterBlob = await fetch(Adjuster).then(res => res.blob());

        const mitigationFile = new File([mitigationBlob], "Mitigation.pdf", { type: "application/pdf" });
        const adjusterFile = new File([adjusterBlob], "Adjuster.pdf", { type: "application/pdf" });

        // Create form data like a real upload
        const formData = new FormData();
        formData.append("file", mitigationFile);
        formData.append("file", adjusterFile);
        formData.append("doc1_name", "Mitigation.pdf");
        formData.append("doc2_name", "Adjuster.pdf");
        formData.append("userId", userData);

        // Upload the files
        const uploadResponse = await fetch(`${process.env.REACT_APP_POST_FILES}`, {
            method: "POST",
            body: formData,
        });

        const uploadResult = await uploadResponse.json();
        const uploaded = uploadResult.uploaded || [];
        const skipped = uploadResult.skipped || [];

        // Now proceed to queue
        const claimDataOverride = {
            adj_email: "adjuster@email.com",
            adj_phone: "555-555-1234",
            adjuster: "Demo Doc1",
            claim_number: "DUNDER MIFFLIN 8593",
            claim_type: "Mitigation",
            date_open: "2025-08-01",
            insurance_comp: "InsureCo",
            status: "Open",
            insured: "Demo Doc2",
            insured_address: "123 Main St, NY",
            insured_phone: "555-555-4321",
            prop_type: "Residential",
            user_id: userData,
            doc1_name: "Mitigation.pdf",
            doc2_name: "Adjuster.pdf"
        };

        const modalDataOverride = {
            header1: { name: 'Mitigation.pdf', count: '123' },
            header2: { name: 'Adjuster.pdf', count: '110' }
        };

        const updatedModalData = {
            ...modalDataOverride,
            claimData: { ...claimDataOverride },
            files: {
                file_1: uploaded[0] || "Mitigation.pdf",
                file_2: uploaded[1] || "Adjuster.pdf",
            },
            timeout: 10000000,
        };

        const queueResponse = await axios.post(`${process.env.REACT_APP_QUEUE}`, updatedModalData);


        const data = queueResponse.data.data[0];

    } catch (error) {
        //console.error("❌ Demo comparison failed:", error);
    }
};

  const [demoButtonDisabled, setDemoButtonDisabled] = useState(false);

  const renderDemoButton = (existingComparisons) => {
    const demoMatch = existingComparisons.find(
      (comp) =>
        comp.claimData?.adjuster === "Demo Doc1" &&
        comp.claimData?.insured === "Demo Doc2"
    );

    if (demoMatch && demoMatch._id) {
      // Comparison exists → always link to full results
      return (
        <button
          style={{
            backgroundColor: '#555555',
            textAlign: 'center',
            boxSizing: 'border-box',
            fontSize: '16px',
            height: '43px',
          }}
          className="create-new-comparison-button"
          disabled={demoButtonDisabled}
        >
          <NavLink
            to={`/restructuredResults/${demoMatch._id}`}
            title="View Sample Results"
            style={{
              color: 'white',
              textDecoration: 'none',
              pointerEvents: demoButtonDisabled ? 'none' : 'auto',
            }}
          >
            View Sample Results
          </NavLink>
        </button>
      );
    }

    // No existing demo → show "Run Demo Comparison"
    return (
      <button
        title="Run a Demo Comparison."
        style={{
          backgroundColor: demoButtonDisabled ? '#888888' : '#555555',
          textAlign: 'center',
          boxSizing: 'border-box',
          fontSize: '16px',
          height: '43px',
          cursor: demoButtonDisabled ? 'not-allowed' : 'pointer',
        }}
        className="create-new-comparison-button"
        onClick={async (e) => {
          setDemoButtonDisabled(true); // disable immediately on click
          await newComparison(e);       // run your demo logic
        }}
        disabled={demoButtonDisabled}
      >
        Run Demo Comparison
      </button>
    );
  };

  // ---------- TABLE ----------
  const renderComparisonsTable = () => {
    if (loading) {
      return (
        <div className="on-load-message-container">
          <p className="loading-comparisons-text">Loading Your Comparisons...</p>
          <div className="loading-spinner"></div>
        </div>
      );
    }

    if (comps.length === 0)
      return (
        <div className="on-load-message-container">
          <p className="navlink-style-start-here-p">
            Hey! Looks like you're new to these parts.{' '}
            <NavLink to="/createNewComparison" className="navlink-style-start-here">
              START HERE.
            </NavLink>
          </p>
        </div>
      );

    const handlePageChange = (newPage) => {
      if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    return (
      <div style={{ overflowX: 'auto' }}>
        <table className="comparisons-table">
          <thead>
            <tr>
              <th>Adjuster Name</th>
              <th>Insured Name</th>
              <th>Date</th>
              <th>Status</th>
              <th>Message</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {comps.map((item, index) => (
              <React.Fragment key={item._id}>
                <tr className="row-wrapper">
                  <td
                    className="ellipsis-cell"
                    onMouseEnter={() => setHoveredCell({ id: item._id + '-adjuster', content: item.claimData.adjuster })}
                    onMouseLeave={() => setHoveredCell({ id: null, content: '' })}
                  >
                    {item.claimData.adjuster}
                  </td>
                  <td
                    className="ellipsis-cell"
                    onMouseEnter={() => setHoveredCell({ id: item._id + '-insured', content: item.claimData.insured })}
                    onMouseLeave={() => setHoveredCell({ id: null, content: '' })}
                  >
                    {item.claimData.insured}
                  </td>
                  <td>{checkDate(item.dateUploaded)}</td>
                  <td>{changeStatusOutput(item)}</td>
                  <td>
                    {item.status === 'Failed' ? (
                      <div
                        style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
                        onClick={() =>
                          setErrorPopup({
                            show: true,
                            content: friendlyErrorMessage(getMessage(item), item._id),
                            compId: item._id,
                          })
                        }
                      >
                        ℹ️ Click for details
                      </div>
                    ) : getMessage(item).includes('%') ? (
                      <div className="progress-wrapper">
                        <span style={{whiteSpace: 'wrap'}}>{getMessage(item)}</span>
                        <div className="progress-container">
                          <div
                            className="progress-bar"
                            style={{ width: extractProgress(getMessage(item)) + '%' }}
                          ></div>
                        </div>
                      </div>
                    ) : (
                      <span style={{whiteSpace: 'wrap'}}>{getMessage(item)}</span>
                    )}
                  </td>
                  <td>{renderActionsMenu(item, index)}</td>
                </tr>
                <tr className={expandedRowIndex === index ? 'details-visible' : 'details-hidden'}>
                  <td colSpan="6" className="details-cell">
                    <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>
                      {expandedRowIndex === index ? JSON.stringify(item, null, 2) : ''}
                    </pre>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>

        <div className="pagination-container">
          <button
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            className="pagination-button"
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            className="pagination-button"
          >
            Next
          </button>
        </div>

        {/* Cell hover popup */}
        {/*         {hoveredCell.id &&
          createPortal(
            <div className="cell-popup">{hoveredCell.content}</div>,
            document.body
          )} */}

        {/* Error popup */}
        {errorPopup.show &&
          createPortal(
            <div className="error-popup">
              <div>{errorPopup.content}</div>
              <button onClick={() => copyComparisonId(errorPopup.compId)}>Copy Comparison ID</button>
              <button onClick={() => setErrorPopup({ show: false, content: '', compId: '' })}>
                Close
              </button>
            </div>,
            document.body
          )}
      </div>
    );
  };

  return (
    <div className="outer-container">
      <MainWrapper />
      <div className="right-container">
        <div className="create-new-claim-container">
          <h1 className="h1-create-new-claim">Active Comparisons</h1>
          <div className="create-new-container">
            <button
              className={`create-new-comparison-button ${comps.length === 0 ? 'attention' : ''}`}
            >
              <NavLink to="/createNewComparison" className="navlink-style-create-new">
                Create New Comparison
              </NavLink>
            </button>
          </div>
          <div className="demo-docs-container">
              {renderDemoButton(comps)}
          </div>          
          <div className="all-comparisons-table">{renderComparisonsTable()}</div>
        </div>
      </div>

      {openMenu.index !== null &&
        createPortal(
          <div
            className="meatball-menu-floating"
            style={{ top: openMenu.top, left: openMenu.left }}
          >
            {checkComparisonStatus(comps[openMenu.index])}
            {user?.email.includes('pdgcreative.com') && (
              <button onClick={() => deleteComparison(comps[openMenu.index]._id)}>DELETE</button>
            )}
            {['@pdgcreative.com', '@getlevlr.com', '@elkmontllc.com'].some((domain) =>
              userEmail.endsWith(domain)
            ) && (
              <button
                onClick={() =>
                  setExpandedRowIndex(
                    expandedRowIndex === openMenu.index ? null : openMenu.index
                  )
                }
              >
                {expandedRowIndex === openMenu.index ? 'HIDE' : 'SHOW'} DETAILS
              </button>
            )}
          </div>,
          document.body
        )}

      <style>
        {`
          .row-wrapper { position: relative; }
          .details-hidden { display: none; }
          .details-visible { display: table-row; background-color: #f9f9f9; }
          .details-cell { padding: 12px; border-top: 1px solid #ccc; }
          .comparisons-table { width: 100%; border-collapse: collapse; }
          .comparisons-table th, .comparisons-table td { padding: 8px; text-align: left; }

          .ellipsis-cell {
            max-width: 180px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            cursor: pointer;
          }

          .cell-popup {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-color: #00B3B5;
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            z-index: 10000;
            max-width: 300px;
            word-break: break-word;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          }

          .error-popup {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-color: #00B3B5;
            color: white;
            padding: 16px 24px;
            border-radius: 8px;
            z-index: 10001;
            max-width: 400px;
            text-align: center;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .error-popup button {
            background: white;
            color: #00B3B5;
            border: none;
            padding: 6px 12px;
            border-radius: 4px;
            cursor: pointer;
            font-weight: bold;
          }

          .pagination-container { display: flex; justify-content: center; align-items: center; margin-top: 1rem; gap: 10px; }
          .pagination-button { background-color: #00B3B5; min-width: 200px; color: white; border: none; padding: 6px 12px; border-radius: 6px; cursor: pointer; }
          .pagination-button:disabled { opacity: 0.5; cursor: not-allowed; }

          .progress-wrapper { display: flex; flex-direction: column; gap: 4px; }
          .progress-container { width: 90%; background-color: #eee; border-radius: 6px; }
          .progress-bar { height: 12px; background-color: #00B3B5; border-radius: 6px; }

          .meatball-container { position: relative; display: inline-block; }
          .meatball-button { background: none; border: none; cursor: pointer; font-size: 20px; }

          .meatball-menu-floating {
            position: fixed;
            background: #00B3B5;
            border: 1px solid #ccc;
            box-shadow: 0 2;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
            z-index: 9999;
            display: flex;
            flex-direction: column;
            padding: 4px 0;
            min-width: 140px;
          }

          .meatball-menu-floating button {
            border: none;
            background: none;
            padding: 8px 16px;
            cursor: pointer;
            text-align: left;
            width: 100%;
            color: white;
          }
        `}
      </style>
    </div>
  );
};

export default ActiveClaims;
